import React, { useCallback, useMemo, useRef, useState } from 'react';
import useToggle from "v4/hooks/useToggle";
import { getLSItem } from "v4/services/localStorage.service";
import { useProspectPageContext } from "v4/contexts/ProspectPageContext";
import useCidImages from "v4/components/form/Field/components/InputRichText/hooks/useCidImages";
import initPlugins from "v4/components/form/Field/components/InputRichText/plugins/plugins";
import GedPopup from "v4/pages/front/Ged/components/GedPopup/GedPopup";

export const TOOLBAR_FULL = [
    { name: '', items: ['undo', 'redo'] },
    { name: '', items: ['formatselect'] },
    {
        name: '',
        items: ['removeformat', 'bold', 'italic', 'underline', 'strikethrough', 'image', 'backcolor', 'forecolor']
    },
    { name: '', items: ['alignleft', 'aligncenter', 'alignright', 'alignjustify'] },
    { name: '', items: ['bullist', 'numlist', 'outdent', 'indent'] },
    { name: '', items: ['link', 'charmap', 'anchor', 'insertdatetime', 'bbcode'] },
    { name: '', items: ['table'] },
    { name: '', items: ['help'] },
];

export const TOOLBAR_RESTRICTED = [
    { name: '', items: ['undo', 'redo'] },
    { name: '', items: ['formatselect'] },
    { name: '', items: ['removeformat', 'bold', 'italic', 'underline', 'strikethrough', 'backcolor', 'forecolor'] },
    { name: '', items: ['alignleft', 'aligncenter', 'alignright', 'alignjustify'] },
    { name: '', items: ['bullist', 'numlist', 'outdent', 'indent'] },
    { name: '', items: ['link'] },
    { name: '', items: ['table'] },
];

/**
 * Dedicated hook for InputRichText logic.
 */
export default function useInputRichText({
    fieldName,
    fieldValue,
    isRestricted,
    onChange,
    formMetadata,
}) {
    const [isShowing, toggleShowing] = useToggle(false);
    const [popupGed, setPopupGed] = useState(null);
    const [isExpanded, setIsExpanded] = useState(false);

    const { processedValue, revert, prepare } = useCidImages(fieldValue, formMetadata.files);

    const initialValue = useRef(null);
    if (initialValue.current === null) {
        initialValue.current = prepare(fieldValue);
    }

    const openGedPicker = useCallback((callback, expansionAllowed, returnType) => {
        toggleShowing();
        setPopupGed(
            <GedPopup
                callback={callback}
                toggle={toggleShowing}
                expansionAllowed={expansionAllowed}
                returnType={returnType}
            />
        );
    }, [toggleShowing]);

    const language = getLSItem('i18nextLng', 'string').includes('fr') ? 'fr' : 'en';
    const { fullname } = getLSItem('user');
    const { prospect } = useProspectPageContext();

    const maxHeight = useMemo(() => window.innerHeight * 0.8, []);

    // Init Custom Plugins
    const {
        pluginList,
        additionalContentCss,
        toolbarWithPlugins
    } = useMemo(() => initPlugins({
        toolbar: isRestricted ? TOOLBAR_RESTRICTED : TOOLBAR_FULL,
        fieldName,
        openGedPicker,
        prospect,
        formMetadata,
    }), [isRestricted, fieldName, openGedPicker, prospect, formMetadata]);

    const handleEditorChange = useCallback((content) => {
        onChange(revert(content));
    }, [onChange, revert]);

    return {
        isShowing,
        popupGed,
        isExpanded,
        setIsExpanded,
        initialValue: initialValue.current,
        processedValue,
        handleEditorChange,
        language,
        fullname,
        maxHeight,
        pluginList,
        additionalContentCss,
        toolbarWithPlugins,
        openGedPicker,
    };
}
